// GAVION task actions: on /tasks, an open task with a compagnie gets « Appeler », « Courriel », « Noter » and
// « Contexte », so the rep works it without opening the compagnie. Mounted from atomic-crm Task.tsx.
import { Info, Mail, NotebookPen, Phone } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useGetOne,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useId, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { contactCallPath, dialablePhones } from "../domain/dialer/contactCall";
import { useLeaseActive } from "../dialer/leaseActive";
import { ComposeEmailDrawer } from "../integrations/compose/ComposeEmailDrawer";
import {
  suggestedRecipients,
  toId,
} from "../integrations/compose/composeModel";
import { callDisabledReason } from "../leads/callDisabledReason";
import type { Lead } from "../leads/types";
import { TaskContextSheet } from "./TaskContextSheet";
import { TaskNoteDialog } from "./TaskNoteDialog";
import { resolveTaskContact } from "./taskRecipient";
import type { ActionTask, TaskContact } from "./taskActionTypes";

/** The actions, or nothing for a done task or a task without a compagnie. */
export const TaskActions = ({ task }: { task: ActionTask }) => {
  if (task.done_date || task.company_id == null || task.company_id === "")
    return null;
  return <OpenTaskActions task={task} companyId={task.company_id} />;
};

const OpenTaskActions = ({
  task,
  companyId,
}: {
  task: ActionTask;
  companyId: Identifier;
}) => {
  const t = useTranslate();
  const [open, setOpen] = useState<"email" | "note" | "context" | null>(null);
  const { data: company } = useGetOne<Lead>("companies", { id: companyId });
  const { data: contact } = useGetOne<TaskContact>(
    "contacts",
    { id: task.contact_id as Identifier },
    { enabled: task.contact_id != null },
  );
  const mailContact = useMailContact(task, companyId, contact);
  return (
    <div
      role="group"
      aria-label={t("gavion.tasks.actions.group", { task: task.text })}
      className="mt-2 flex flex-wrap items-start gap-2"
    >
      <CallAction companyId={companyId} company={company} contact={contact} />
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen("email")}
      >
        <Mail className="size-4" aria-hidden="true" />
        {t("gavion.tasks.actions.email")}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen("note")}
      >
        <NotebookPen className="size-4" aria-hidden="true" />
        {t("gavion.tasks.actions.note")}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen("context")}
      >
        <Info className="size-4" aria-hidden="true" />
        {t("gavion.tasks.actions.context")}
      </Button>
      {open === "email" ? (
        <ComposeEmailDrawer
          open
          onOpenChange={(next) => !next && setOpen(null)}
          companyId={toId(companyId)}
          contactId={mailContact ? toId(mailContact.id) : null}
          dealId={toId(task.deal_id ?? null)}
          defaultTo={suggestedRecipients(mailContact, company ?? null)}
        />
      ) : null}
      <TaskNoteDialog
        task={task}
        companyId={companyId}
        company={company}
        open={open === "note"}
        onOpenChange={(next) => setOpen(next ? "note" : null)}
      />
      {open === "context" ? (
        <TaskContextSheet
          task={task}
          companyId={companyId}
          open
          onOpenChange={(next) => !next && setOpen(null)}
        />
      ) : null}
    </div>
  );
};

/**
 * Who « Courriel » writes to: the task's contact, else the deal's first contact (only fetched when the task has a
 * deal), else the compagnie's first contact with an email (only fetched when the deal gave none).
 */
const useMailContact = (
  task: ActionTask,
  companyId: Identifier,
  own: TaskContact | undefined,
): TaskContact | null => {
  const needs = task.contact_id == null;
  const hasDeal = needs && task.deal_id != null;
  const { data: deal, isPending: dealPending } = useGetOne<{
    id: Identifier;
    contact_ids?: Identifier[] | null;
  }>("deals", { id: task.deal_id as Identifier }, { enabled: hasDeal });
  const dealContactId = deal?.contact_ids?.[0];
  const { data: dealContact } = useGetOne<TaskContact>(
    "contacts",
    { id: dealContactId as Identifier },
    { enabled: dealContactId != null },
  );
  const { data: companyContacts } = useGetList<TaskContact>(
    "contacts",
    {
      filter: { company_id: companyId },
      pagination: { page: 1, perPage: 25 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: needs && !(hasDeal && dealPending) && dealContactId == null },
  );
  return resolveTaskContact(own, dealContact, companyContacts);
};

/**
 * « Appeler »: the task's contact on his own number when he has one, else the compagnie's main line; the dialer
 * comes back to this page after the outcome. Disabled (with the reason) like the lead header's button.
 */
const CallAction = ({
  companyId,
  company,
  contact,
}: {
  companyId: Identifier;
  company: Lead | undefined;
  contact: TaskContact | undefined;
}) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const reasonId = useId();
  const { identity } = useGetIdentity();
  const leaseActive = useLeaseActive(
    company?.locked_by,
    company?.locked_until,
    identity?.id,
  );
  const { data: holder } = useGetOne(
    "sales",
    { id: company?.locked_by as Identifier },
    { enabled: leaseActive },
  );
  const holderName = holder
    ? `${holder.first_name} ${holder.last_name ?? ""}`.trim()
    : null;
  const contactPhone = contact ? dialablePhones(contact.phone_jsonb)[0] : null;
  const reason = company
    ? callDisabledReason(
        company,
        { leaseActive, holderName, checkMainPhone: !contactPhone },
        t,
      )
    : null;
  const path =
    contact && contactPhone
      ? contactCallPath(companyId, contact.id, contactPhone.e164)
      : `/appeler?company_id=${companyId}`;
  const button = (
    <Button
      type="button"
      size="sm"
      // Until the compagnie is read, its call rules are unknown.
      disabled={!company || !!reason}
      onClick={() =>
        navigate(path, {
          state: { from: location.pathname + location.search },
        })
      }
    >
      <Phone className="size-4" aria-hidden="true" />
      {t("gavion.tasks.actions.call")}
    </Button>
  );
  if (!reason) return button;
  return (
    <div className="flex flex-col items-start gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} aria-describedby={reasonId}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
      {/* Always in the DOM for assistive tech; visible on narrow screens where there is no hover. */}
      <p id={reasonId} className="text-xs text-muted-foreground sm:sr-only">
        {reason}
      </p>
    </div>
  );
};
